// "＋ Gösterge" seçici (Sete ekle): katalogda olup sette olmayan metrikler. Sektör paketleri
// DR1/DR3/DR8: Set kur ile aynı iki sekmeli `PackageSourceTabs` (Paket / Katalog — TEK seçim kümesi);
// paket satırı "N eksiği seç" yalnız seçer, tek yazan düğme "Sete ekle (N)" → TEK
// `POST /kpi/sets/:setId/indicators/bulk {packageIds, metricIds}` (sunucu eksik ataları aynı tx'te
// ekler; eski metrik başına sıralı istek döngüsü kalktı). İstek sürerken seçim kilitli, hata sonrası
// korunur; başarı "N gösterge eklendi · M üst kategori" + formül girdisi eksikse amber bilgi satırı.
// Kırılım (spec §6.3): kırılımlı gösterge seçilince grupları + üyeleri otomatik işaretlenir (tek
// tek kaldırılabilir), üyesiz grup sete alınmaz, kısmi seçimde "kırılımın N üyesi sette yok" uyarısı.

import { useEffect, useMemo, useState } from "react";
import type { KpiMetric, KpiSetBulkAddResult, KpiSetIndicator } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { bulkAddKpiSetIndicators, listKpiMetrics } from "../../api/kpi";
import { usePackageSelection } from "../../hooks/use-package-selection";
import { breakdownErrorText, KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { breakdownPickPlan, togglePickWithBreakdown } from "../../lib/kpi-breakdown-picker";
import {
  emptySelection,
  packageInSetCount,
  packageMissingCount,
  packageUnion,
  summarize,
} from "../../lib/kpi-package-selection";
import {
  isPackageErrorCode,
  KPI_PACKAGES_COPY,
  packageErrorText,
  packageNamesText,
} from "../../lib/kpi-packages-copy";
import { buildIndicatorTree, visibleIdsForSearch } from "../../lib/kpi-set-editor";
import { useKpiStore } from "../../stores/kpi-store";
import { LoadingState } from "../common/StateSurface";
import { UnresolvedRefsNote } from "./ForkSummaryCard";
import { KpiModal } from "./KpiModal";
import {
  codeStyle,
  errorBox,
  footerBtn,
  helpText,
  inputStyle,
  labelStyle,
  pillStyle,
} from "./kpi-form-styles";
import { PackageSourceTabs, type PackageRowInfo } from "./PackageSourceTabs";

const PA = KPI_PACKAGES_COPY.pickerAction;

export interface AddIndicatorPickerProps {
  open: boolean;
  setId: string;
  clientId: string;
  indicators: KpiSetIndicator[];
  onClose: () => void;
  onAdded: () => void;
}

/** Tek yazıcı istek (DR3): paket iz bağı + açık üyelik aynı gövdede; boş alan gönderilmez. */
export function submitBulkAdd(
  setId: string,
  packageIds: readonly string[],
  metricIds: readonly string[],
): Promise<KpiSetBulkAddResult> {
  return bulkAddKpiSetIndicators(setId, {
    ...(packageIds.length > 0 ? { packageIds: [...packageIds] } : {}),
    ...(metricIds.length > 0 ? { metricIds: [...metricIds] } : {}),
  });
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    return isPackageErrorCode(err.code)
      ? packageErrorText(err.code, err.message, err.details)
      : breakdownErrorText(err.code, err.message);
  }
  return err instanceof Error ? err.message : KPI_PACKAGES_COPY.errorFallback;
}

export function AddIndicatorPicker(props: AddIndicatorPickerProps): JSX.Element | null {
  const { open, setId, clientId, indicators, onClose, onAdded } = props;
  const [metrics, setMetrics] = useState<KpiMetric[] | null>(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KpiSetBulkAddResult | null>(null);
  const clientSector = useKpiStore((s) => s.clients.find((c) => c.id === clientId)?.sector);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setError(null);
    setResult(null);
    setMetrics(null);
    let cancelled = false;
    listKpiMetrics({ clientId })
      .then((res) => {
        if (!cancelled) setMetrics(res.metrics);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Katalog yüklenemedi");
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  const inSet = useMemo(() => new Set(indicators.map((i) => i.metricId)), [indicators]);
  // Sete ekle hiçbir zaman ön seçim yapmaz; etkin paket varsa Paket sekmesinde açılır
  const sel = usePackageSelection({
    open,
    catalog: metrics,
    inSet,
    clientSector,
    opening: (_ctx, packages) => ({
      tab: packages && packages.length > 0 ? "package" : "catalog",
      state: emptySelection(),
    }),
  });
  const picked = sel.state.selected;
  const tree = useMemo(() => buildIndicatorTree(metrics ?? []), [metrics]);
  const visible = useMemo(() => visibleIdsForSearch(tree, search), [tree, search]);
  const rows = useMemo(
    () => tree.rows.filter((r) => !visible || visible.has(r.node.id)),
    [tree, visible],
  );
  const plan = useMemo(
    () => breakdownPickPlan(metrics ?? [], picked, inSet),
    [metrics, picked, inSet],
  );
  const summary = useMemo(
    () => summarize(sel.state, sel.ctx, { packages: sel.packages ?? [] }),
    [sel.state, sel.ctx, sel.packages],
  );
  const union = useMemo(
    () => packageUnion(sel.state.checked, sel.ctx),
    [sel.state.checked, sel.ctx],
  );
  const infos = useMemo(() => {
    const out = new Map<string, PackageRowInfo>();
    if (metrics === null) return out;
    for (const p of sel.packages ?? []) {
      out.set(p.id, {
        missing: packageMissingCount(p.id, sel.ctx),
        ...packageInSetCount(p.id, sel.ctx),
      });
    }
    return out;
  }, [metrics, sel.packages, sel.ctx]);
  const blocked = sel.deprecatedChecked.length > 0;
  const n = plan.ids.length;
  const checkedNames = (sel.packages ?? [])
    .filter((p) => sel.state.checked.includes(p.id))
    .map((p) => p.name);
  // Bağ notu düğmenin hemen altında, tek satır ("A, B ve C" / "A +3 paket")
  const boundNote = checkedNames.length > 0 ? PA.boundNote(packageNamesText(checkedNames)) : null;
  const describedBy =
    [blocked ? "picker-blocked-reason" : null, boundNote ? "picker-bound-note" : null]
      .filter(Boolean)
      .join(" ") || null;

  async function submit(): Promise<void> {
    if (!metrics || n === 0 || blocked) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await submitBulkAdd(setId, sel.state.checked, plan.ids);
      setResult(res);
      sel.setState(emptySelection());
      onAdded();
    } catch (err) {
      // tek tx — kısmi ekleme yok; seçim korunur
      if (err instanceof ApiError && err.code === "KPI_PACKAGE_HAS_DEPRECATED") {
        const pid = (err.details as { packageId?: unknown } | undefined)?.packageId;
        void sel.showDeprecated(typeof pid === "string" ? pid : null);
      }
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <KpiModal
      open={open}
      kicker="VERİ SETİNE GÖSTERGE EKLE"
      title="Katalogdan gösterge seç"
      description="Veri setinde olmayan göstergeler listelenir; eksik üst kategoriler otomatik eklenir."
      onClose={onClose}
      width={680}
      footer={
        <>
          {blocked ? (
            <span
              id="picker-blocked-reason"
              className="mr-auto"
              style={{ ...helpText, color: "var(--color-warning-text)" }}
            >
              {KPI_PACKAGES_COPY.deprecated.blockedReason}
            </span>
          ) : null}
          <button type="button" style={footerBtn()} onClick={onClose} disabled={busy}>
            {result ? PA.close : PA.cancel}
          </button>
          <div className="grid gap-1" style={{ justifyItems: "end", minWidth: 0 }}>
            <button
              type="button"
              style={{ ...footerBtn(true), opacity: n > 0 && !busy && !blocked ? 1 : 0.5 }}
              disabled={n === 0 || busy || blocked}
              aria-busy={busy || undefined}
              {...(describedBy ? { "aria-describedby": describedBy } : {})}
              onClick={() => void submit()}
            >
              {busy ? KPI_PACKAGES_COPY.summary.adding(n) : PA.addToSet(n)}
            </button>
            {boundNote ? (
              <span
                id="picker-bound-note"
                data-testid="picker-bound-note"
                title={boundNote}
                style={{
                  ...helpText,
                  maxWidth: 420,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {boundNote}
              </span>
            ) : null}
          </div>
        </>
      }
    >
      <PackageSourceTabs
        idPrefix="picker-src"
        mode="picker"
        tab={sel.tab}
        onTabChange={sel.setTab}
        packages={sel.packages}
        loadFailed={sel.loadFailed}
        suggested={sel.suggested}
        showNoSuggestion={sel.showNoSuggestion}
        checked={sel.state.checked}
        {...(metrics !== null
          ? { rowInfo: (id: string) => infos.get(id) ?? { missing: 0, inSet: 0, total: 0 } }
          : {})}
        onToggle={sel.toggle}
        onRecheck={() => void sel.recheck()}
        disabled={busy}
        catalogCounts={
          sel.hasActivePackages ? { added: summary.added, removed: summary.removed } : null
        }
        focusRequest={sel.focusRequest}
        ready={sel.ready}
        catalogPanel={
          <div className="grid gap-1">
            <label style={labelStyle} htmlFor="picker-search">
              Katalogda ara
            </label>
            <input
              id="picker-search"
              type="search"
              placeholder="Katalogda ara…"
              style={{ ...inputStyle, width: "100%" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div
              className="rounded-lg"
              style={{ border: "1px solid var(--color-border)", maxHeight: 320, overflowY: "auto" }}
            >
              {metrics === null && !error ? <LoadingState label="Katalog yükleniyor…" /> : null}
              {metrics !== null && rows.length === 0 ? (
                <div className="px-3 py-4 text-sm" style={{ color: "var(--color-text-3)" }}>
                  Eşleşen gösterge yok
                </div>
              ) : null}
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {rows.map((r) => {
                  const m = r.node;
                  const already = inSet.has(m.id);
                  const isCat = r.hasChildren;
                  return (
                    <li
                      key={m.id}
                      className="flex items-center gap-2"
                      style={{
                        padding: "6px 12px",
                        paddingLeft: 12 + r.depth * 22,
                        borderBottom: "1px solid var(--color-border)",
                        fontSize: "0.875rem",
                        minHeight: 40,
                        background: isCat ? "var(--color-bg)" : "transparent",
                        color: already ? "var(--color-text-3)" : "var(--color-text)",
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={m.label}
                        checked={picked.has(m.id) || already}
                        disabled={already || busy}
                        onChange={(e) =>
                          sel.catalogChange(
                            togglePickWithBreakdown(
                              metrics ?? [],
                              picked,
                              m.id,
                              e.target.checked,
                              inSet,
                            ),
                          )
                        }
                      />
                      <span
                        className="min-w-0 flex-1 truncate"
                        style={{ fontWeight: isCat ? 600 : 400 }}
                      >
                        {m.label}
                        <span style={codeStyle}>{m.code}</span>
                        {union.has(m.id) ? (
                          <span style={{ ...pillStyle, marginLeft: 6 }}>
                            {KPI_PACKAGES_COPY.pills.package}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: "var(--color-text-3)", whiteSpace: "nowrap" }}
                      >
                        {already ? "veri setinde" : (m.defaultUnit ?? "")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        }
      />
      <div className="grid gap-1" style={{ marginTop: 10 }}>
        <span style={helpText} data-testid="picker-counts" aria-live="polite">
          {KPI_PACKAGES_COPY.summary.counts(inSet.size, n, summary.ancestors)}
        </span>
      </div>
      <BreakdownPickNotes plan={plan} />
      <PickerResult result={result} />
      {error ? (
        <div style={{ ...errorBox, marginTop: 10 }} role="alert">
          {error}
        </div>
      ) : null}
    </KpiModal>
  );
}

/** Başarı satırı + formül girdisi eksik amber bilgi (engellemez). */
export function PickerResult(p: { result: KpiSetBulkAddResult | null }): JSX.Element | null {
  if (!p.result) return null;
  const { added, ancestorsAdded, unresolvedFormulaRefs } = p.result;
  return (
    <div className="grid gap-2" style={{ marginTop: 10 }}>
      <p role="status" style={{ margin: 0, fontWeight: 600 }}>
        {KPI_PACKAGES_COPY.summary.done(added, ancestorsAdded)}
      </p>
      <UnresolvedRefsNote refs={unresolvedFormulaRefs} />
    </div>
  );
}

/** Kırılım seçim notları: otomatik işaret bilgisi, eksik üye uyarısı (amber), atlanan üyesiz grup. */
export function BreakdownPickNotes(props: {
  plan: ReturnType<typeof breakdownPickPlan>;
}): JSX.Element | null {
  const { plan } = props;
  if (!plan.hasBreakdown && plan.skippedGroups === 0) return null;
  const P = KPI_BREAKDOWN_COPY.picker;
  const amber = { color: "var(--color-warning-text)" } as const;
  return (
    <div className="text-sm grid gap-1" style={{ marginTop: 10 }} aria-live="polite">
      {plan.hasBreakdown ? (
        <span style={{ color: "var(--color-text-3)" }}>{P.autoChecked}</span>
      ) : null}
      {plan.missingMembers > 0 ? (
        <span style={amber} data-testid="kpi-picker-missing-members">
          {KPI_BREAKDOWN_COPY.missingMembers(plan.missingMembers)} — {P.partialHint}
        </span>
      ) : null}
      {plan.skippedGroups > 0 ? (
        <span style={amber}>{P.emptyGroupSkipped(plan.skippedGroups)}</span>
      ) : null}
    </div>
  );
}
